import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Globe, Users, Target, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default function About() {
  const navigate = useNavigate();

  const values = [
    { icon: Heart, title: 'Health First', desc: 'Every decision we make puts your wellbeing at the center.' },
    { icon: Globe, title: 'Globally Inclusive', desc: 'Nutrition that respects and celebrates every culture.' },
    { icon: Users, title: 'Community Driven', desc: 'We grow together, learn together, succeed together.' },
    { icon: Target, title: 'Science-Backed', desc: 'Every recommendation grounded in peer-reviewed research.' },
  ];

  const team = [
    { role: 'Founder & CEO', quote: 'We believe fitness should be a right, not a privilege.' },
    { role: 'Head of Nutrition', quote: 'Food is medicine. We make it personal.' },
    { role: 'Chief Technology Officer', quote: 'AI should empower, not replace human potential.' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl">VYTA</span>
          </Link>
          <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-6">
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full bg-[#CCFF00] opacity-[0.03] blur-[150px]" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm text-zinc-400 mb-6">
              <Sparkles className="w-4 h-4 text-[#CCFF00]" />
              Our Story
            </span>
          </motion.div>

          <motion.h1 
            initial="hidden" animate="visible" variants={fadeUp}
            className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight mb-8"
          >
            Making Health
            <br />
            <span className="gradient-text">Accessible to All</span>
          </motion.h1>

          <motion.p 
            initial="hidden" animate="visible" variants={fadeUp}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            We started VYTA with a simple belief: everyone deserves access to world-class fitness and nutrition guidance, 
            regardless of where they live, what they can afford, or how they eat.
          </motion.p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="relative p-12 rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#CCFF00] opacity-5 rounded-full blur-[100px]" />
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-black mb-6">Our Mission</h2>
                <p className="text-lg text-zinc-300 leading-relaxed mb-6">
                  For too long, premium fitness coaching and personalized nutrition planning have been reserved for 
                  elite athletes and those who can afford personal trainers. <strong className="text-white">We're changing that.</strong>
                </p>
                <p className="text-lg text-zinc-300 leading-relaxed mb-6">
                  VYTA harnesses the power of artificial intelligence to deliver the same quality of personalized 
                  programming that professional athletes receive—<span className="text-[#CCFF00]">at a fraction of the cost.</span>
                </p>
                <p className="text-lg text-zinc-300 leading-relaxed">
                  But we don't just make it affordable. We make it <strong className="text-white">culturally relevant</strong>. 
                  Your nutrition plan won't ask you to abandon the foods you love. Instead, we build around them—creating 
                  meal plans that celebrate your heritage while optimizing your performance.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-black/50 border border-zinc-800">
                  <div className="text-5xl font-black text-[#CCFF00] mb-2">150+</div>
                  <div className="text-zinc-400">Countries with active users</div>
                </div>
                <div className="p-6 rounded-2xl bg-black/50 border border-zinc-800">
                  <div className="text-5xl font-black text-[#CCFF00] mb-2">13</div>
                  <div className="text-zinc-400">Cuisine traditions supported</div>
                </div>
                <div className="p-6 rounded-2xl bg-black/50 border border-zinc-800">
                  <div className="text-5xl font-black text-[#CCFF00] mb-2">1M+</div>
                  <div className="text-zinc-400">Personalized meals generated</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Problem We're Solving */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">The Problem We're Solving</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              The fitness industry has failed most people. Here's why we're different.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800">
              <div className="text-6xl mb-4">🏋️</div>
              <h3 className="text-xl font-bold mb-3">Generic Programs Don't Work</h3>
              <p className="text-zinc-400">
                Cookie-cutter workout plans ignore your body type, schedule, equipment access, and limitations. 
                We build every program specifically for <em>you</em>.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800">
              <div className="text-6xl mb-4">🥗</div>
              <h3 className="text-xl font-bold mb-3">Nutrition Plans Ignore Culture</h3>
              <p className="text-zinc-400">
                Most diet plans are built for Western palates. If you're from Asia, Africa, South America, or anywhere 
                else—your food traditions matter. We honor them.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
              className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800">
              <div className="text-6xl mb-4">💸</div>
              <h3 className="text-xl font-bold mb-3">Quality Costs Too Much</h3>
              <p className="text-zinc-400">
                Personal trainers charge $100-300/hour. Nutritionists cost thousands annually. We deliver the same 
                personalization for less than the cost of a coffee per day.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">What We Stand For</h2>
            <p className="text-zinc-400 text-lg">The principles that guide everything we build</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <motion.div 
                key={value.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#CCFF00]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-[#CCFF00]" />
                </div>
                <h3 className="text-lg font-bold mb-2">{value.title}</h3>
                <p className="text-zinc-400 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Nutrition */}
      <section className="py-20 bg-gradient-to-b from-transparent via-[#CCFF00]/5 to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-4xl font-black mb-6">
                Nutrition That Speaks
                <br />
                <span className="gradient-text">Your Language</span>
              </h2>
              <p className="text-lg text-zinc-300 mb-6">
                When we say personalized nutrition, we mean it. Your meal plans aren't just adjusted for macros—they're 
                crafted around your cultural food traditions.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-[#CCFF00] text-xl">→</span>
                  <span className="text-zinc-300"><strong className="text-white">Chinese users</strong> get balanced meals featuring authentic stir-fries, congee, and dim sum-inspired dishes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#CCFF00] text-xl">→</span>
                  <span className="text-zinc-300"><strong className="text-white">Indian users</strong> receive plans built around dals, rotis, and regional specialties</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#CCFF00] text-xl">→</span>
                  <span className="text-zinc-300"><strong className="text-white">Latin American users</strong> enjoy plans featuring traditional ingredients and cooking styles</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#CCFF00] text-xl">→</span>
                  <span className="text-zinc-300"><strong className="text-white">Everyone</strong> gets the same scientific precision, wrapped in familiar flavors</span>
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="grid grid-cols-3 gap-4">
              {['🇨🇳', '🇯🇵', '🇰🇷', '🇮🇳', '🇲🇽', '🇧🇷', '🇮🇹', '🇫🇷', '🇹🇭'].map((flag, i) => (
                <div key={i} className="aspect-square rounded-2xl bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-5xl hover:scale-110 transition-transform cursor-default">
                  {flag}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Join the Movement */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl font-black mb-6">
              Join the <span className="gradient-text">Movement</span>
            </h2>
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
              Thousands of people across the globe are already transforming their lives with VYTA. 
              Your journey to a healthier, happier you starts with a single step.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-10 py-6 text-lg">
                Start Your Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/science')} className="border-zinc-700 hover:bg-zinc-900 rounded-full px-10 py-6 text-lg">
                Read the Science
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black">V</span>
            </div>
            <span className="font-bold">VYTA</span>
          </div>
          <div className="flex gap-8 text-sm text-zinc-500">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/science" className="hover:text-white transition-colors">Science</Link>
          </div>
          <p className="text-sm text-zinc-600">© 2025 VYTA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
